import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Spinner, Toast } from '../../lib';
import apiServices from '../../services/apiServices';
import { GetSupportBoard, SupportTicket } from '../../services/dtos';
import {
  TICKET_QUEUES,
  TicketAgeDot,
  TicketCategoryBadge,
  TicketEscalatedBadge,
  TicketStatusBadge,
  TicketUrgentBadge,
  fmtDateTime,
  roleLabel,
  ticketAnswered,
} from './supportShared';

// T5: the global board (vision Feature 2) — at-a-glance visibility into
// where every ticket sits: who has it, how long, answered or not. The server
// returns only tickets the viewer can work; confidential tickets drop out
// per viewer exactly as in the queues.
const SupportBoardPage: React.FC = () => {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [includeDismissed, setIncludeDismissed] = useState(false);
  const [includeResolved, setIncludeResolved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const req = new GetSupportBoard();
    req.includeDismissed = includeDismissed;
    apiServices.client
      .get(req)
      .then((data: SupportTicket[]) => {
        setTickets(data);
        setLoading(false);
      })
      .catch((e: any) => {
        setError(e?.responseStatus?.message || 'Failed to load the board');
        setLoading(false);
      });
  }, [includeDismissed]);

  const shown = useMemo(
    () =>
      includeResolved ? tickets : tickets.filter((t) => t.status !== 'Resolved'),
    [tickets, includeResolved]
  );

  const open = shown.filter(
    (t) => t.status !== 'Resolved' && t.status !== 'Dismissed'
  );
  const unanswered = open.filter((t) => !ticketAnswered(t));

  return (
    <div className="mx-auto w-full flex max-w-6xl flex-col gap-6 dark:text-white">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link
            to="/help/queue"
            className="text-sm text-indigo-600 dark:text-indigo-400"
          >
            ← Back to the queue
          </Link>
          <h2 className="mt-1 text-2xl font-bold">Support Board</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Every request you can see, across all queues — {open.length} open,{' '}
            {unanswered.length} waiting on staff.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            <input
              type="checkbox"
              checked={includeResolved}
              onChange={(e) => setIncludeResolved(e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600"
            />
            Show resolved
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            <input
              type="checkbox"
              checked={includeDismissed}
              onChange={(e) => setIncludeDismissed(e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600"
            />
            Show dismissed
          </label>
        </div>
      </div>

      {error && <Toast color="failure">{error}</Toast>}
      {loading && <Spinner color="info" />}

      {!loading && !error && shown.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-8 text-center text-sm text-gray-500 dark:text-gray-400">
          Nothing on the board. 🎉
        </div>
      )}

      {shown.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="px-3 py-2 w-14">#</th>
                <th className="px-3 py-2">Subject</th>
                <th className="px-3 py-2 w-40">Type</th>
                <th className="px-3 py-2 w-40">Where it sits</th>
                <th className="px-3 py-2 w-40">From</th>
                <th className="px-3 py-2 w-36">Who has it</th>
                <th className="px-3 py-2 w-32">Waiting</th>
                <th className="px-3 py-2 w-28">Status</th>
                <th className="px-3 py-2 w-40">Opened</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-gray-900">
              {shown.map((t) => (
                <tr key={t.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-3 py-2 text-gray-400 text-xs">{t.id}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Link
                        to={`/help/ticket/${t.id}`}
                        className="font-medium text-indigo-600 dark:text-indigo-400 break-words"
                      >
                        {t.subject}
                      </Link>
                      <TicketUrgentBadge isUrgent={t.isUrgent} />
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <TicketCategoryBadge category={t.category} />
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300">
                    {t.escalatedToRole ? (
                      <TicketEscalatedBadge toRole={t.escalatedToRole} />
                    ) : (
                      TICKET_QUEUES.find((q) => q.name === t.queue)?.label ||
                      roleLabel(t.queue)
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {/* S1: the member's running record */}
                    <Link
                      to={`/help/member/${t.memberUniqueMemberId}`}
                      className="text-gray-700 hover:text-indigo-600 hover:underline dark:text-gray-300 dark:hover:text-indigo-400"
                    >
                      {t.memberName}
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-gray-500 dark:text-gray-400">
                    {t.ownerName || '—'}
                  </td>
                  <td className="px-3 py-2">
                    <TicketAgeDot ticket={t} />
                  </td>
                  <td className="px-3 py-2">
                    <TicketStatusBadge status={t.status} />
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                    {fmtDateTime(t.createdAtUtc)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SupportBoardPage;
